import './style.scss';
import type { IJourney } from '../../../../models/journey.model';
import WheelchairIcon from '../../WheelchairIcon';
import { JourneyHelper } from '../../../../helpers/journey.helper';
import { StopAreaHelper } from '../../../../helpers/stop-area.helper';
import LineIcon from '../../LineIcon';
import MicromobilityTransportTrip from '../MicromobilityTransportTrip';
import NonPublicTransportTrip from '../NonPublicTransportTrip';
import { TranslationService } from '../../../../services/translation.service';
import { DateHelper } from '../../../../helpers/date.helper';
import Occupancy from '../../Occupancy';
import NotesIcons from './components/NotesIcons';
import { WheelchairIconType } from '../../WheelchairIcon/wheelchair-icon-type.enum';

function JourneySummary({ journey, useEstimatedTime = false, showNotesIcons = false }: { journey: IJourney; useEstimatedTime?: boolean; showNotesIcons?: boolean }) {
  // === VARIABLES ===
  const occupancy = JourneyHelper.getJourneyOccupancy(journey);
  const firstLeg = JourneyHelper.getFirstPublicTransportLeg(journey);
  const originStopPointName = journey.destinationLink?.origin.name ?? firstLeg?.origin.stopPoint.name ?? '';
  const originStopPointPlatform = firstLeg?.origin.stopPoint.platform ?? '';
  const isDepartureOnTime = !useEstimatedTime || JourneyHelper.isDepartureOnTime(journey);
  const isArrivalOnTime = !useEstimatedTime || JourneyHelper.isArrivalOnTime(journey);
  const isTripLegCancelled = useEstimatedTime && JourneyHelper.getTripLegIsCancelled(journey);
  const isTripLegPartiallyCancelled = useEstimatedTime && JourneyHelper.getTripLegIsPartCancelled(journey);
  const descriptionActualDepartureTime = `${useEstimatedTime && !isDepartureOnTime ? `${TranslationService.get('travelplanner.estimated')} ` : ''}${TranslationService.get('common.departuretime')}`;
  const descriptionActualArrivalTime = `${useEstimatedTime && !isArrivalOnTime ? `${TranslationService.get('travelplanner.estimated')} ` : ''}${TranslationService.get('common.arrivaltime')}`;
  const journeyWheelchairAccessibility = JourneyHelper.getJourneyWheelchairAccessibility(journey);

  return (
    <div className="journey-search-results-journey-summary d-flex justify-content-between flex-nowrap">
      <div className="d-flex flex-nowrap flex-column flex-grow-1">
        <div className="d-flex align-items-center mb-1 flex-wrap">
          {journey.startingMicromobilityLink ? (
            <span className="h3 m-0 d-inline-block text-wordbreak-break-all me-1">
              {StopAreaHelper.getNameWithoutMunicipality(journey.startingMicromobilityLink.fromSearchLocationName ?? TranslationService.get('travelplanner.micromobility.vehicleposition'))}
            </span>
          ) : (
            <>
              <span className="h3 m-0 d-inline-block text-wordbreak-break-all me-1">{StopAreaHelper.getNameWithoutMunicipality(originStopPointName)}</span>
              {originStopPointPlatform && (
                <span className={`fs-3 text-color-gray-800 ${journeyWheelchairAccessibility ? 'me-1' : ''}`}>
                  {TranslationService.get('common.platform')} {originStopPointPlatform}
                </span>
              )}
              {journeyWheelchairAccessibility && <WheelchairIcon iconType={WheelchairIconType.dark} sizeInRem={1.5} />}
            </>
          )}
        </div>
        {occupancy && !journey.destinationLink && !journey.startingMicromobilityLink && (journey.allLegs?.length ?? 0) > 0 && <Occupancy occupancy={occupancy} className="mb-1" />}

        <div className="d-flex flex-wrap align-items-center mb-n1">
          {showNotesIcons && <NotesIcons notes={JourneyHelper.getAllNotes(journey)} className="me-1 mb-1" />}
          {journey.destinationLink ? (
            <NonPublicTransportTrip destinationLink={journey.destinationLink} className="mb-1" />
          ) : (
            <>
              {journey.startingMicromobilityLink && <MicromobilityTransportTrip className="mb-1" micromobilityLink={journey.startingMicromobilityLink} showWithVehicleCount={true} />}
              {journey.tripLegs
                ?.filter((tripLeg) => tripLeg.serviceJourney.line)
                .map((tripLeg) => (
                  <LineIcon key={tripLeg.serviceJourney.gid} line={tripLeg.serviceJourney.line!} className="me-1 mb-1" />
                ))}
              {journey.endingMicromobilityLink && <MicromobilityTransportTrip className="mb-1" micromobilityLink={journey.endingMicromobilityLink} showWithVehicleCount={true} />}
            </>
          )}
        </div>
      </div>
      <div className="journey-search-results-journey-summary__right-column text-end flex-grow-0 flex-shrink-1">
        <span className={`h3 m-0 text-nowrap d-flex fw-semi-bold justify-content-end ${!isArrivalOnTime ? 'me-n1' : ''}`}>
          <span>
            <span className="visually-hidden">{descriptionActualDepartureTime}:</span>
            <span className={`${!isDepartureOnTime ? 'bg-yellow pe-1 ps-1' : ''}`}>{DateHelper.getTimeFromDateTimeString(JourneyHelper.getDepartureTimeForJourney(journey, useEstimatedTime))}</span>
            {!isDepartureOnTime && <br />}
            {!isDepartureOnTime && (
              <span className="journey-search-results-journey-summary__time--is-not-on-time fw-normal text-center d-block">
                <span className="visually-hidden">
                  {TranslationService.get('travelplanner.planned')} {TranslationService.get('common.departuretime')}:
                </span>
                {DateHelper.getTimeFromDateTimeString(JourneyHelper.getDepartureTimeForJourney(journey, false))}
              </span>
            )}
          </span>
          <span aria-hidden="true"> - </span>
          <span>
            <span className="visually-hidden">{descriptionActualArrivalTime}:</span>
            <span className={`${!isArrivalOnTime ? 'bg-yellow pe-1 ps-1' : ''}`}>{DateHelper.getTimeFromDateTimeString(JourneyHelper.getArrivalTimeForJourney(journey, useEstimatedTime))}</span>
            {!isArrivalOnTime && <br />}
            {!isArrivalOnTime && (
              <span className="journey-search-results-journey-summary__time--is-not-on-time fw-normal text-center d-block">
                <span className="visually-hidden">
                  {TranslationService.get('travelplanner.planned')} {TranslationService.get('common.arrivaltime')}:
                </span>
                {DateHelper.getTimeFromDateTimeString(JourneyHelper.getArrivalTimeForJourney(journey, false))}
              </span>
            )}
          </span>
        </span>
        {!isTripLegCancelled && !isTripLegPartiallyCancelled && (
          <>
            {!isTripLegCancelled && (
              <div className="text-nowrap pt-1">
                {TranslationService.get('common.traveltime')} {DateHelper.formatAsHoursAndMinutes(JourneyHelper.getDurationInMinutes(journey, useEstimatedTime))}
              </div>
            )}
          </>
        )}
        {isTripLegCancelled && <div className="text-danger pt-1">{TranslationService.get('common.cancelled')}</div>}
        {!isTripLegCancelled && isTripLegPartiallyCancelled && <div className="text-danger pt-1">{TranslationService.get('common.partcancelled')}</div>}
      </div>
    </div>
  );
}

export default JourneySummary;

